/**
 * The audit queue's tiers (#1292), mirrored for display.
 *
 * ⚠️ This does NOT decide the order — `GET /demands?stage=new&sort=review`
 * does, in SQL, so that it holds across pages. Sorting here would only order
 * the rows that happened to come back, which is the truncation #1269 is about.
 *
 * What this file does is put a *name* on the tier a row is already in, so the
 * grouped table can say why the order is the order. The rules below have to
 * agree with `services/demand_review_order.py`; the test that walks a
 * server-ordered page and asserts the tiers never go backwards is what catches
 * it when they stop agreeing.
 */

import type { Demand } from '../types/demand'

/** Below this a suggestion is not worth showing as an answer. Mirrors
 *  `LOW_CONFIDENCE_BELOW` in `services/demand_review_order.py`. */
export const LOW_CONFIDENCE_BELOW = 0.5

export const UNCLASSIFIED_FOCUS_AREA = 'UN'

export type ReviewTier =
  | 'overridden'
  | 'untrusted'
  | 'unclassified'
  | 'settled'
  | 'confirmed'
  | 'routed'

/** Most suspect first — the same order the server sorts by. */
export const REVIEW_TIER_ORDER: ReviewTier[] = [
  'overridden',
  'untrusted',
  'unclassified',
  'settled',
  'confirmed',
  'routed',
]

export const REVIEW_TIER_LABELS: Record<ReviewTier, { name: string }> = {
  // Only rows submitted before 2026-09-11 can be here: since then the server
  // routes every submission and the requestor is shown no answer to disagree with.
  overridden: {
    name: 'Requestor disagreed',
  },
  untrusted: {
    name: 'Nothing to trust',
  },
  unclassified: {
    name: 'Nobody knew',
  },
  settled: {
    // Named rather than hidden on purpose. These rows are the reason the queue
    // lists everything instead of filtering to the flagged ones: the case no
    // flag catches is the classifier and the submitter agreeing, confidently,
    // and both being wrong.
    name: 'Settled',
  },
  confirmed: {
    name: 'Confirmed ✓',
  },
  routed: {
    name: 'Routed onward',
  },
}

/**
 * What to SAY about a row. The tier's own label, except for the one case where
 * it would be false: a "settled" row whose landing differs from the suggestion
 * was not agreed with at all — a person moved it, and "agreed and confident"
 * is exactly what did not happen (#1375). It keeps its place in the order;
 * only the words change.
 */
export function reviewTierLabel(demand: Demand): { name: string } {
  const tier = reviewTier(demand)
  if (
    tier === 'settled' &&
    demand.suggested_focus_area &&
    demand.suggested_focus_area !== demand.focus_area_code
  ) {
    return { name: 'Moved by a person' }
  }
  return REVIEW_TIER_LABELS[tier]
}

export function reviewTier(demand: Demand): ReviewTier {
  // First, deliberately. Once a demand is routed onward it is not sitting in a
  // focus area, so none of the tiers below describe it — calling it "settled,
  // agreed and confident" would have the page assert something false.
  if (demand.disposition) return 'routed'
  // Second: a human vouched ("Looks right ✓"), which resolves whatever the
  // flags below would say. Persisted since 2026-09-10 — it used to be session
  // ticks, and the queue asked the same question again every visit.
  if (demand.landing_confirmed_at) return 'confirmed'
  if (demand.suggestion_overridden === true) return 'overridden'
  if (demand.suggestion_confidence == null) return 'untrusted'
  if (demand.suggestion_confidence < LOW_CONFIDENCE_BELOW) return 'untrusted'
  if (demand.focus_area_code === UNCLASSIFIED_FOCUS_AREA) return 'unclassified'
  return 'settled'
}

